import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import OnboardingFlow from './OnboardingFlow'
import {
  extractFirstName,
  fetchFormattedCurrentGames,
  getUserOrRedirect,
  isNewUser,
} from './homePageHelpers'
import supabase from '@/supabaseClient'

interface Game {
  id: string
  title: string
  image: string
  progress: number
  platforms: string[]
  genres: string[]
  status: string
  nextIntent?: string
  nextNote?: string
}

const toCoverImageUrl = (imageUrl?: string) => {
  if (!imageUrl) return '/default-image.jpg'
  if (!imageUrl.includes('images.igdb.com/igdb/image/upload')) return imageUrl
  if (imageUrl.includes('t_1080p')) return imageUrl
  return imageUrl
    .replace('/t_screenshot_big/', '/t_1080p/')
    .replace('/t_cover_big/', '/t_1080p/')
    .replace('/t_thumb/', '/t_1080p/')
}

const HomePage = () => {
  const navigate = useNavigate()
  const [currentGames, setCurrentGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [userName, setUserName] = useState<string>('')
  const [updatingGameId, setUpdatingGameId] = useState<string | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [buddyQuery, setBuddyQuery] = useState('')
  const [buddyWhy, setBuddyWhy] = useState('')

  const statusOptions = [
    'Currently Playing',
    'Done',
    'Endless',
    'Satisfied',
    'DNF',
    'Wishlist',
    'Try Again',
    'Started',
    'Owned',
    'Come back!',
  ]

  const updateGameStatus = async (gameId: string, nextStatus: string) => {
    setUpdatingGameId(gameId)
    setStatusError(null)

    const { error } = await supabase
      .from('user_games')
      .update({ status: nextStatus, updated_at: new Date().toISOString() })
      .eq('game_id', gameId)

    if (error) {
      console.error('Error updating status:', error.message)
      setStatusError('Could not update status. Please try again.')
      setUpdatingGameId(null)
      return
    }

    setCurrentGames((prev) => {
      if (nextStatus !== 'Currently Playing') {
        return prev.filter((game) => game.id !== gameId)
      }
      return prev.map((game) =>
        game.id === gameId ? { ...game, status: nextStatus } : game
      )
    })

    setUpdatingGameId(null)
  }

  useEffect(() => {
    const loadUserHomepage = async () => {
      try {
        const user = await getUserOrRedirect(navigate)
        if (!user) return

        setUserName(extractFirstName(user))

        setShowOnboarding(await isNewUser(user.id))

        setCurrentGames(await fetchFormattedCurrentGames(user.id))
      } catch (error) {
        console.error('Error fetching current games:', error)
      } finally {
        setLoading(false)
      }
    }

    loadUserHomepage()
  }, [navigate])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading loading-spinner loading-lg" role="status"></div>
      </div>
    )
  }

  if (showOnboarding) {
    return (
      <OnboardingFlow
        onComplete={() => {
          setShowOnboarding(false)
          navigate('/app/library')
        }}
        userName={userName}
      />
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <p className="font-serif text-lg mb-6" style={{ color: '#a68565' }}>
        {userName ? `Hey ${userName}. ✦` : 'Hey. ✦'}
      </p>

      {/* Zone 1: Backlog Buddy */}
      <div className="rounded-2xl p-6 mb-8" style={{ background: '#fdf8f2', border: '1px solid #ecd5b8' }}>
        <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: '#c4714a' }}>
          Backlog Buddy
        </p>
        <h1 className="text-2xl font-bold font-serif italic mb-5" style={{ color: '#3d2a1e' }}>
          Before you buy that —<br />check what you already own.
        </h1>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (buddyQuery.trim()) navigate('/app/purchase-intent', { state: { intent: buddyQuery, intentWhy: buddyWhy } })
          }}
          className="flex flex-col gap-3"
        >
          <input
            type="text"
            placeholder="What are you about to buy?"
            value={buddyQuery}
            onChange={(e) => setBuddyQuery(e.target.value)}
            className="input rounded-full w-full"
            style={{ background: '#fdf8f2', border: '1px solid #ecd5b8' }}
            autoComplete="off"
          />
          {buddyQuery.trim() && (
            <textarea
              placeholder="What's pulling you toward it? The vibe, the look, something a friend mentioned..."
              value={buddyWhy}
              onChange={(e) => setBuddyWhy(e.target.value)}
              className="textarea w-full resize-none rounded-2xl"
              style={{ background: '#fdf8f2', border: '1px solid #ecd5b8' }}
              rows={2}
            />
          )}
          <div>
            <button
              type="submit"
              className="btn btn-primary rounded-full px-8"
              disabled={!buddyQuery.trim()}
            >
              Find a match ✦
            </button>
          </div>
        </form>
      </div>

      {/* Zone 2: What you're playing */}
      <div className="mb-8">
        <h2 className="font-semibold font-serif mb-3" style={{ color: '#3d2a1e' }}>
          What you're playing
        </h2>
        {statusError && (
          <div className="alert alert-error mb-4">
            <span>Something went wrong. Give it another try.</span>
          </div>
        )}
        {currentGames.length === 0 ? (
          <p className="text-base-content/40 text-sm">Nothing on the go. Want to find something tonight? ✦</p>
        ) : (
          <div className="space-y-2">
            {currentGames.map((game) => {
              const coverImage = toCoverImageUrl(game.image)
              return (
                <div
                  key={game.id}
                  className="flex items-center gap-3 rounded-xl p-3 cursor-pointer hover:bg-base-200 transition-colors"
                  onClick={() => navigate(`/app/game/${game.id}`)}
                >
                  <img
                    src={coverImage}
                    alt={game.title}
                    className="w-10 h-14 object-cover rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: '#3d2a1e' }}>{game.title}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="w-24 h-1 rounded-full overflow-hidden shrink-0" style={{ background: '#ecd5b8' }}>
                        <div
                          className="h-1 bg-primary rounded-full transition-all duration-300"
                          style={{ width: `${game.progress || 0}%` }}
                        />
                      </div>
                      <span className="text-xs" style={{ color: '#a68565' }}>
                        {game.progress || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Zone 3: Shuffle */}
      <div className="border-t pt-8 text-center" style={{ borderColor: '#ecd5b8' }}>
        <p className="text-sm mb-3" style={{ color: '#a68565' }}>Nothing feeling right tonight?</p>
        <button
          className="btn btn-outline rounded-full px-8"
          onClick={() => navigate('/app/explore')}
        >
          Shuffle ✦
        </button>
      </div>
    </div>
  )
}

export default HomePage
